/**
 * JobCompletionToaster
 *
 * Headless, app-shell-level component that raises a toast the first time any
 * tracked job reaches a terminal state — so every job gets baseline completion
 * feedback even if the user has navigated away from a foreground progress view.
 * Renders nothing; mount once inside the authenticated tree (and ToastProvider).
 */

import { useEffect, useRef } from 'react';

import {
  JOB_PROGRESS_CONSTANTS,
  JobProgressUtils,
} from '@personal-trainer-app/shared';

import { resolveJobCompletion } from './jobCompletion';

import { useToast } from '@/components/ui/Toast';
import { usePlanReviewSync } from '@/hooks/questionnaire';
import { useJobProgressStore } from '@/stores';

export function JobCompletionToaster(): null {
  const jobs = useJobProgressStore((state) => state.jobs);
  const { success, error } = useToast();
  const toastedRef = useRef<Set<string>>(new Set());

  // A job ending held, or a review notification, moves `plan_review` on the
  // session user; this is the one always-mounted place that watches jobs.
  usePlanReviewSync();

  useEffect(() => {
    if (!JOB_PROGRESS_CONSTANTS.LIVE_JOB_PROGRESS) return;

    for (const job of Object.values(jobs)) {
      if (!JobProgressUtils.isTerminalJobStatus(job.status)) continue;
      if (toastedRef.current.has(job.job_id)) continue;
      toastedRef.current.add(job.job_id);

      const feedback = resolveJobCompletion(job);
      if (!feedback) continue;

      type ToastOptions = NonNullable<Parameters<typeof success>[1]>;
      const options: ToastOptions = {};
      if (feedback.title) options.title = feedback.title;
      if (feedback.action) {
        const { href, onClick, label } = feedback.action;
        options.action = {
          label,
          onClick: () => {
            if (onClick) {
              onClick();
            } else if (href) {
              window.open(href, '_blank', 'noopener,noreferrer');
            }
          },
        };
      }

      if (feedback.variant === 'error') {
        error(feedback.message, options);
      } else {
        success(feedback.message, options);
      }
    }
  }, [jobs, success, error]);

  return null;
}

export default JobCompletionToaster;
